import React, { useEffect, useState } from "react";
import { usePlaidLink } from "react-plaid-link";
import {
  CONNECT_INTEGRATION,
  GET_INTEGRATION_TOKEN,
} from "../../graphql/integration";
import { GET_EXTERNAL_PENDING_CARDS } from "../../graphql/card";
import { useLazyQuery, useMutation } from "@apollo/client";
import MapUserExternalCardDrawer, {
  Card,
} from "../admin/common/MapUserExternalCard";
import { IntegrationType } from "../../enums/IntegrationType";
import { toast } from "react-toastify";
import LoadingWithoutModal from "./LoadingWithoutModal";
import { useTranslation } from "react-i18next";

export enum PlaidAction {
  ADD_ACCOUNT = "ADD",
  RECONNECT = "RECONNECT",
  EDIT_ACCOUNT = "EDIT",
}
interface PlaidLinkWrapperProps {
  visible: boolean;
  existingAccountId?: string | null;
  action?: PlaidAction;
  onSuccess: () => void;
  onModalClosed: () => void;
}

const PlaidLinkWrapper: React.FC<PlaidLinkWrapperProps> = ({
  visible,
  existingAccountId,
  action,
  onSuccess,
  onModalClosed,
}) => {
  const { t } = useTranslation();

  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [isUserCardMapOpen, setIsUserCardMapOpen] = useState<boolean>(false);
  const [cards, setCards] = useState<Card[]>([]);
  const [accountId, setAccountId] = useState<string | null>(null);

  const [getPlaidIntegrationToken] = useMutation(GET_INTEGRATION_TOKEN, {
    variables: {
      type: IntegrationType.PLAID_EXTERNAL_CARD_ACCOUNT,
      ...(existingAccountId && { accountId: existingAccountId }),
    },
    onCompleted: (data) => {
      setLinkToken(data?.integrationToken?.linkToken || null);
    },
    onError: (err) => {
      toast.error(t("Failed to connect to Plaid Please try again later"));
    },
  });

  const [getExternalPendingCards, { loading: loadingPendingCards }] =
    useLazyQuery(GET_EXTERNAL_PENDING_CARDS, {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        const updatedCards = data.pendingExternalCardDetails.map(
          (card: Card) => {
            return {
              ...card,
              ...(card?.possibleCards?.length === 1 && {
                selectedCard: card.possibleCards[0].id,
                user: card.possibleCards[0].user,
              }),
            };
          },
        );

        setCards(updatedCards);
        setIsUserCardMapOpen(true);
      },
      onError: (err) => {
        toast.error(
          t(
            "It is taking longer than normal to retrieve your cards Try again in a few minutes",
          ),
        );
      },
    });

  const [connectIntegration, { loading: loadingConnection }] = useMutation(
    CONNECT_INTEGRATION,
    {
      onCompleted: (data) => {
        if (data?.connectIntegration) {
          if (existingAccountId === data?.connectIntegration) {
            onSuccess();
            toast.success(t("Plaid integration reconnected successfully"));
          } else {
            setAccountId(data?.connectIntegration);
            getExternalPendingCards({
              variables: {
                accountId: data?.connectIntegration,
              },
            });
          }
        } else {
          toast.error(t("Failed to connect Plaid integration"));
        }
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  );

  useEffect(() => {
    if (visible) {
      if (
        action === PlaidAction.ADD_ACCOUNT ||
        action === PlaidAction.RECONNECT
      ) {
        // Fetch the link token when visible
        getPlaidIntegrationToken();
      } else if (action === PlaidAction.EDIT_ACCOUNT && existingAccountId) {
        setAccountId(existingAccountId);
        getExternalPendingCards({
          variables: {
            accountId: existingAccountId,
          },
        });
      }
    }
  }, [getPlaidIntegrationToken, visible, action, existingAccountId]);

  const handleModalClosed = () => {
    setLinkToken(null);
    setCards([]);
    setAccountId(null);
    onModalClosed();
  };

  return (
    <>
      {linkToken && visible && (
        <PlaidLinkChild
          linkToken={linkToken}
          visible={visible}
          onSuccess={(public_token, metadata) => {
            connectIntegration({
              variables: {
                type: IntegrationType.PLAID_EXTERNAL_CARD_ACCOUNT,
                key: public_token,
              },
            });
            handleModalClosed();
          }}
          onExit={() => {
            handleModalClosed();
          }}
        />
      )}
      <MapUserExternalCardDrawer
        accountId={accountId}
        open={isUserCardMapOpen}
        cards={cards}
        isEdit={action === PlaidAction.EDIT_ACCOUNT}
        onSuccess={() => {
          setLinkToken(null);
          setCards([]);
          setAccountId(null);
          setIsUserCardMapOpen(false);
          onSuccess();
        }}
        onExit={() => {
          setIsUserCardMapOpen(false);
          handleModalClosed();
        }}
      />
      <LoadingWithoutModal open={loadingPendingCards || loadingConnection} />
    </>
  );
};

interface PlaidLinkChildProps {
  linkToken: string;
  visible: boolean;
  onSuccess: (public_token: string, metadata: any) => void;
  onExit: (error: Error | null, metadata: any) => void;
}

const PlaidLinkChild: React.FC<PlaidLinkChildProps> = ({
  linkToken,
  visible,
  onSuccess,
  onExit,
}) => {
  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess,
    onExit: (error: any, metadata: any) => {
      onExit && onExit(error, metadata);
    },
  });

  useEffect(() => {
    if (visible && ready) {
      open();
    }
  }, [visible, ready, open]);

  return null;
};

export default PlaidLinkWrapper;
